import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils';
import {
    COURT_W, COURT_H, HALF_W, HALF_H, GOAL_W, GOAL_H, GOAL_LINE_Y,
    CORNER_R, BOARD_H, GLASS_H, GOAL_POST_H,
} from './constants';
import {
    makeFloorTexture, makeAdTexture, makeNetTexture, makeScoreboardTexture, rng,
} from './textures';

const BOARD_T = 0.16;
const NET_CELL = 0.11;
const NET_TOP_DEPTH = 0.45;

function roundedRectShape(hw, hh, r) {
    const s = new THREE.Shape();
    s.moveTo(-hw + r, -hh);
    s.lineTo(hw - r, -hh);
    s.absarc(hw - r, -hh + r, r, -Math.PI / 2, 0, false);
    s.lineTo(hw, hh - r);
    s.absarc(hw - r, hh - r, r, 0, Math.PI / 2, false);
    s.lineTo(-hw + r, hh);
    s.absarc(-hw + r, hh - r, r, Math.PI / 2, Math.PI, false);
    s.lineTo(-hw, -hh + r);
    s.absarc(-hw + r, -hh + r, r, Math.PI, Math.PI * 1.5, false);
    return s;
}

function roundedRectPath(hw, hh, r) {
    const p = new THREE.Path();
    const s = roundedRectShape(hw, hh, r);
    p.curves = s.curves;
    return p;
}

// Extruded ring between two rounded rectangles (boards, rails, glass)
function ringGeometry(innerOff, outerOff, z0, z1) {
    const outer = roundedRectShape(HALF_W + outerOff, HALF_H + outerOff, CORNER_R + outerOff);
    outer.holes.push(roundedRectPath(HALF_W + innerOff, HALF_H + innerOff, CORNER_R + innerOff));
    const geo = new THREE.ExtrudeGeometry(outer, { depth: z1 - z0, bevelEnabled: false, curveSegments: 20 });
    geo.translate(0, 0, z0);
    return geo;
}

export default class Arena {
    constructor(scene, { tier = 'high', aniso = 4 } = {}) {
        this.scene = scene;
        this.group = new THREE.Group();
        this.group.name = 'arena';
        scene.add(this.group);
        this._tier = tier;
        this._disposables = [];

        this._buildLights();
        this._buildFloor(aniso);
        this._buildBoards();
        this._goals = [this._buildGoal(1), this._buildGoal(-1)];
        this._buildStands();
        this._buildRigging();
        this._buildScoreboard();
    }

    _track(...objs) { this._disposables.push(...objs); return objs[0]; }

    // ─── Lights ───────────────────────────────────────────────────────────────
    _buildLights() {
        const hemi = new THREE.HemisphereLight(0xb8d4ff, 0x1a2230, 0.55);
        this.group.add(hemi);

        // Key light — the only shadow caster, fitted tightly around the rink
        const key = new THREE.DirectionalLight(0xfff4e6, 1.9);
        key.position.set(7, -9, 22);
        key.castShadow = true;
        const sc = key.shadow.camera;
        sc.left = -HALF_W - 3; sc.right = HALF_W + 3;
        sc.top = HALF_H + 3;   sc.bottom = -HALF_H - 3;
        sc.near = 5; sc.far = 50;
        key.shadow.mapSize.set(this._tier === 'high' ? 2048 : 1024, this._tier === 'high' ? 2048 : 1024);
        key.shadow.bias = -0.0004;
        key.shadow.normalBias = 0.02;
        key.shadow.radius = 3;
        this.group.add(key, key.target);
        this.keyLight = key;

        // Overhead arena spots (no shadows) — give pooled, broadcast-style lighting
        const spotPos = this._tier === 'high'
            ? [[-5, -9], [5, -9], [-5, 9], [5, 9]]
            : [[0, -8], [0, 8]];
        this._spots = [];
        spotPos.forEach(([x, y]) => {
            const s = new THREE.SpotLight(0xeef4ff, this._tier === 'high' ? 0.7 : 1.1, 0, 0.75, 0.8, 0);
            s.userData.base = s.intensity;
            this._spots.push(s);
            s.position.set(x, y, 14);
            s.target.position.set(x * 0.6, y * 0.6, 0);
            this.group.add(s, s.target);
        });

        // Goal lamp light (top goal — where shots go)
        this.goalLight = new THREE.PointLight(0xff2a1a, 0, 9, 0);
        this.goalLight.position.set(0, HALF_H - 0.2, BOARD_H + GLASS_H + 0.5);
        this.group.add(this.goalLight);
    }

    // ─── Floor ────────────────────────────────────────────────────────────────
    _buildFloor(aniso) {
        // Outer concrete
        const ground = new THREE.Mesh(
            this._track(new THREE.PlaneGeometry(160, 160)),
            this._track(new THREE.MeshStandardMaterial({ color: 0x0b1320, roughness: 0.92 }))
        );
        ground.position.z = -0.02;
        ground.receiveShadow = true;
        this.group.add(ground);

        // Rink surface — rounded rect with UVs remapped to the full court
        const geo = new THREE.ShapeGeometry(roundedRectShape(HALF_W, HALF_H, CORNER_R), 24);
        const pos = geo.attributes.position, uv = geo.attributes.uv;
        for (let i = 0; i < pos.count; i++) {
            uv.setXY(i, (pos.getX(i) + HALF_W) / COURT_W, (pos.getY(i) + HALF_H) / COURT_H);
        }
        this._track(geo);
        const tex = this._track(makeFloorTexture(aniso));
        const mat = this._track(new THREE.MeshPhysicalMaterial({
            map: tex,
            roughness: 0.42,
            metalness: 0.0,
            clearcoat: 0.35,
            clearcoatRoughness: 0.3,
            envMapIntensity: 0.45,
        }));
        const floor = new THREE.Mesh(geo, mat);
        floor.receiveShadow = true;
        this.group.add(floor);
    }

    // ─── Boards, rails, glass, ads ────────────────────────────────────────────
    _buildBoards() {
        const boardMat = this._track(new THREE.MeshStandardMaterial({ color: 0xeef1f5, roughness: 0.38, metalness: 0.02 }));
        const kickMat  = this._track(new THREE.MeshStandardMaterial({ color: 0xf2c200, roughness: 0.5 }));
        const railMat  = this._track(new THREE.MeshStandardMaterial({ color: 0x1d5fc4, roughness: 0.35, metalness: 0.1 }));
        const glassMat = this._track(new THREE.MeshPhysicalMaterial({
            color: 0xcfe6ff, roughness: 0.06, metalness: 0.0, transparent: true, opacity: 0.09,
            depthWrite: false, side: THREE.DoubleSide, envMapIntensity: 1.4,
        }));
        const postMat  = this._track(new THREE.MeshStandardMaterial({ color: 0xc9d1dc, roughness: 0.3, metalness: 0.6 }));

        const boards = new THREE.Mesh(this._track(ringGeometry(0, BOARD_T, 0, BOARD_H)), boardMat);
        boards.castShadow = true; boards.receiveShadow = true;
        const kick   = new THREE.Mesh(this._track(ringGeometry(-0.012, 0.0, 0, 0.17)), kickMat);
        const rail   = new THREE.Mesh(this._track(ringGeometry(-0.03, BOARD_T + 0.05, BOARD_H, BOARD_H + 0.06)), railMat);
        rail.castShadow = true;
        const glass  = new THREE.Mesh(this._track(ringGeometry(0.06, 0.075, BOARD_H + 0.06, BOARD_H + 0.06 + GLASS_H)), glassMat);
        glass.renderOrder = 2;
        this.group.add(boards, kick, rail, glass);

        // Glass stanchions around the perimeter
        const perim = roundedRectShape(HALF_W + 0.07, HALF_H + 0.07, CORNER_R + 0.07);
        const pts = perim.getSpacedPoints(44);
        const stanGeo = this._track(new THREE.BoxGeometry(0.05, 0.05, GLASS_H));
        const stan = new THREE.InstancedMesh(stanGeo, postMat, pts.length);
        const m = new THREE.Matrix4();
        pts.forEach((p, i) => {
            m.makeTranslation(p.x, p.y, BOARD_H + 0.06 + GLASS_H / 2);
            stan.setMatrixAt(i, m);
        });
        this.group.add(stan);

        // Advertising panels on the straight inner faces
        const adZ0 = 0.2, adH = 0.42;
        const panels = [
            { len: COURT_H - 2 * CORNER_R - 0.6, x:  HALF_W - 0.004, y: 0, rot: -Math.PI / 2, seed: 0 },
            { len: COURT_H - 2 * CORNER_R - 0.6, x: -HALF_W + 0.004, y: 0, rot:  Math.PI / 2, seed: 2 },
            { len: COURT_W - 2 * CORNER_R - 0.6, x: 0, y:  HALF_H - 0.004, rot: 0,       seed: 4 },
            { len: COURT_W - 2 * CORNER_R - 0.6, x: 0, y: -HALF_H + 0.004, rot: Math.PI, seed: 1 },
        ];
        panels.forEach(p => {
            const tex = this._track(makeAdTexture(p.seed));
            tex.repeat.set(p.len / 12, 1);
            tex.wrapS = THREE.RepeatWrapping;
            const geo = this._track(new THREE.PlaneGeometry(p.len, adH));
            geo.rotateX(Math.PI / 2);
            geo.rotateZ(p.rot);
            const mesh = new THREE.Mesh(geo, this._track(new THREE.MeshStandardMaterial({ map: tex, roughness: 0.45 })));
            mesh.position.set(p.x, p.y, adZ0 + adH / 2);
            mesh.receiveShadow = true;
            this.group.add(mesh);
        });
    }

    // ─── Goals ────────────────────────────────────────────────────────────────
    // Built in a local frame: mouth on y=0, net extending to +y. side=+1 → top goal.
    _buildGoal(side) {
        const g = new THREE.Group();
        g.position.set(0, side * GOAL_LINE_Y, 0);
        g.rotation.z = side > 0 ? 0 : Math.PI;
        this.group.add(g);

        const hw = GOAL_W / 2, H = GOAL_POST_H, D = GOAL_H;
        const redMat   = this._track(new THREE.MeshStandardMaterial({ color: 0xd8141f, roughness: 0.28, metalness: 0.45 }));
        const whiteMat = this._track(new THREE.MeshStandardMaterial({ color: 0xf2f2f2, roughness: 0.4, metalness: 0.2 }));

        const poly = (pts, r, mat) => {
            const path = new THREE.CurvePath();
            for (let i = 0; i < pts.length - 1; i++) path.add(new THREE.LineCurve3(pts[i], pts[i + 1]));
            const geo = this._track(new THREE.TubeGeometry(path, 24 * (pts.length - 1), r, 10, false));
            const mesh = new THREE.Mesh(geo, mat);
            mesh.castShadow = true;
            g.add(mesh);
            return mesh;
        };
        const V = (x, y, z) => new THREE.Vector3(x, y, z);
        // Red front frame with rounded top corners
        const frontCurve = new THREE.CurvePath();
        frontCurve.add(new THREE.LineCurve3(V(-hw, 0, 0), V(-hw, 0, H - 0.1)));
        frontCurve.add(new THREE.QuadraticBezierCurve3(V(-hw, 0, H - 0.1), V(-hw, 0, H), V(-hw + 0.1, 0, H)));
        frontCurve.add(new THREE.LineCurve3(V(-hw + 0.1, 0, H), V(hw - 0.1, 0, H)));
        frontCurve.add(new THREE.QuadraticBezierCurve3(V(hw - 0.1, 0, H), V(hw, 0, H), V(hw, 0, H - 0.1)));
        frontCurve.add(new THREE.LineCurve3(V(hw, 0, H - 0.1), V(hw, 0, 0)));
        const front = new THREE.Mesh(this._track(new THREE.TubeGeometry(frontCurve, 120, 0.055, 12, false)), redMat);
        front.castShadow = true;
        g.add(front);
        // White base & back frame
        poly([V(-hw, 0, 0.03), V(-hw, D, 0.03), V(hw, D, 0.03), V(hw, 0, 0.03)], 0.035, whiteMat);
        [-hw, hw].forEach(x => poly([V(x, 0, H), V(x, NET_TOP_DEPTH, H * 0.97), V(x, D, 0.03)], 0.03, whiteMat));
        poly([V(-hw, NET_TOP_DEPTH, H * 0.97), V(hw, NET_TOP_DEPTH, H * 0.97)], 0.03, whiteMat);

        // Netting
        const netTex = this._track(makeNetTexture());
        const netMat = this._track(new THREE.MeshStandardMaterial({
            color: 0xffffff, alphaMap: netTex, transparent: true, alphaTest: 0.35, side: THREE.DoubleSide,
            roughness: 0.9, depthWrite: true,
        }));

        // Bag: u across width, v along profile crossbar → top-back → base-back
        const prof = [[0, H], [NET_TOP_DEPTH, H * 0.97], [D, 0.03]];
        const segLen = [Math.hypot(prof[1][0] - prof[0][0], prof[1][1] - prof[0][1]),
                        Math.hypot(prof[2][0] - prof[1][0], prof[2][1] - prof[1][1])];
        const profLen = segLen[0] + segLen[1];
        const NU = 24, NV = 14;
        const positions = [], uvs = [], indices = [];
        for (let j = 0; j <= NV; j++) {
            const s = (j / NV) * profLen;
            let d, z;
            if (s <= segLen[0]) { const a = s / segLen[0]; d = prof[0][0] + (prof[1][0] - prof[0][0]) * a; z = prof[0][1] + (prof[1][1] - prof[0][1]) * a; }
            else { const a = (s - segLen[0]) / segLen[1]; d = prof[1][0] + (prof[2][0] - prof[1][0]) * a; z = prof[1][1] + (prof[2][1] - prof[1][1]) * a; }
            for (let i = 0; i <= NU; i++) {
                const u = i / NU;
                const sag = 0.06 * Math.sin(Math.PI * u) * Math.sin(Math.PI * j / NV);
                positions.push(-hw + GOAL_W * u, d + sag, z + sag * 0.3);
                uvs.push((GOAL_W * u) / NET_CELL, s / NET_CELL);
            }
        }
        for (let j = 0; j < NV; j++) for (let i = 0; i < NU; i++) {
            const a = j * (NU + 1) + i, b = a + 1, c = a + NU + 1, d2 = c + 1;
            indices.push(a, c, b, b, c, d2);
        }
        const bagGeo = this._track(new THREE.BufferGeometry());
        bagGeo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        bagGeo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
        bagGeo.setIndex(indices);
        bagGeo.computeVertexNormals();
        const bag = new THREE.Mesh(bagGeo, netMat);
        bag.castShadow = true;
        g.add(bag);

        // Side panels
        [-hw, hw].forEach(x => {
            const sh = new THREE.Shape();
            sh.moveTo(0, 0.03); sh.lineTo(0, H); sh.lineTo(NET_TOP_DEPTH, H * 0.97); sh.lineTo(D, 0.03); sh.closePath();
            const geo = this._track(new THREE.ShapeGeometry(sh));
            const uv = geo.attributes.uv;
            for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) / NET_CELL, uv.getY(i) / NET_CELL);
            geo.rotateX(Math.PI / 2);
            geo.rotateZ(Math.PI / 2);
            geo.scale(-1, 1, 1);
            const mesh = new THREE.Mesh(geo, netMat);
            mesh.position.x = x;
            g.add(mesh);
        });

        // Goal lamp on top of the glass behind the net
        const lampMat = this._track(new THREE.MeshStandardMaterial({ color: 0x550808, emissive: 0xff1a0a, emissiveIntensity: 0.08, roughness: 0.3 }));
        const lamp = new THREE.Mesh(this._track(new THREE.SphereGeometry(0.22, 18, 10, 0, Math.PI * 2, 0, Math.PI / 2)), lampMat);
        lamp.rotation.x = Math.PI / 2;
        lamp.position.set(0, HALF_H - GOAL_LINE_Y + 0.08, BOARD_H + 0.06 + GLASS_H + 0.06);
        const lampBase = new THREE.Mesh(this._track(new THREE.CylinderGeometry(0.24, 0.26, 0.08, 18)), whiteMat);
        lampBase.rotation.x = Math.PI / 2;
        lampBase.position.copy(lamp.position).setZ(lamp.position.z - 0.03);
        g.add(lamp, lampBase);

        return {
            group: g, side, bag, lampMat,
            basePos: Float32Array.from(positions),
            rippleAmp: 0,
        };
    }

    // ─── Stands + crowd ───────────────────────────────────────────────────────
    _buildStands() {
        const rows  = this._tier === 'high' ? 12 : 8;
        const STEP_D = 0.9, STEP_H = 0.45;
        const gap = BOARD_T + 1.8;
        const geos = [];
        const seats = [];   // {x, y, z} seat positions for crowd

        const sides = [
            { axis: 'x', sign:  1, len: COURT_H - 1 },
            { axis: 'x', sign: -1, len: COURT_H - 1 },
            { axis: 'y', sign:  1, len: COURT_W - 1 },
            { axis: 'y', sign: -1, len: COURT_W - 1 },
        ];
        sides.forEach(s => {
            const base = (s.axis === 'x' ? HALF_W : HALF_H) + gap;
            for (let r = 0; r < rows; r++) {
                const off = base + r * STEP_D + STEP_D / 2;
                const top = 0.5 + r * STEP_H;
                const geo = s.axis === 'x'
                    ? new THREE.BoxGeometry(STEP_D, s.len, top)
                    : new THREE.BoxGeometry(s.len, STEP_D, top);
                if (s.axis === 'x') geo.translate(s.sign * off, 0, top / 2);
                else                geo.translate(0, s.sign * off, top / 2);
                geos.push(geo);
                const spacing = 0.56;
                const n = Math.floor(s.len / spacing);
                for (let k = 0; k < n; k++) {
                    const along = -s.len / 2 + (k + 0.5) * spacing;
                    if (s.axis === 'x') seats.push([s.sign * (off + 0.1), along, top]);
                    else                seats.push([along, s.sign * (off + 0.1), top]);
                }
            }
        });
        // Corner wedges so the bowl reads as continuous
        const cornerGeo = new THREE.CylinderGeometry(rows * STEP_D * 0.9, rows * STEP_D * 0.9, rows * STEP_H * 0.6, 12, 1, false, 0, Math.PI / 2);
        cornerGeo.rotateX(Math.PI / 2);
        [[1, 1], [-1, 1], [-1, -1], [1, -1]].forEach(([sx, sy], i) => {
            const c = cornerGeo.clone();
            c.rotateZ(i * Math.PI / 2);
            c.translate(sx * (HALF_W + gap - 0.4), sy * (HALF_H + gap - 0.4), rows * STEP_H * 0.3);
            geos.push(c);
        });
        cornerGeo.dispose();

        const merged = this._track(mergeGeometries(geos));
        geos.forEach(g => g.dispose());
        const standMat = this._track(new THREE.MeshStandardMaterial({ color: 0x121c2c, roughness: 0.95 }));
        const stands = new THREE.Mesh(merged, standMat);
        stands.receiveShadow = true;
        this.group.add(stands);

        // Crowd — one InstancedMesh, per-instance colours
        const rand = rng(5);
        const density = this._tier === 'high' ? 0.86 : 0.55;
        const filled = seats.filter(() => rand() < density);
        const body = new THREE.CapsuleGeometry(0.15, 0.2, 3, 8);
        body.rotateX(Math.PI / 2);
        body.translate(0, 0, 0.28);
        const head = new THREE.SphereGeometry(0.1, 8, 6);
        head.translate(0, 0, 0.62);
        const personGeo = this._track(mergeGeometries([body, head]));
        body.dispose(); head.dispose();
        const crowdMat = this._track(new THREE.MeshLambertMaterial({ color: 0xffffff }));
        const crowd = new THREE.InstancedMesh(personGeo, crowdMat, filled.length);
        const palette = [0x1d5fc4, 0x1d5fc4, 0xd8232f, 0xf2f2f2, 0x222831, 0x3b4252, 0xf2c200, 0x6b7a90, 0x8a5a44, 0x2e7d32];
        const col = new THREE.Color();
        const mtx = new THREE.Matrix4();
        this._crowdBase = [];
        filled.forEach(([x, y, z], i) => {
            const sc = 0.9 + rand() * 0.2;
            mtx.makeScale(sc, sc, sc).setPosition(x, y, z);
            crowd.setMatrixAt(i, mtx);
            col.setHex(palette[Math.floor(rand() * palette.length)]).multiplyScalar(0.3 + rand() * 0.3);
            crowd.setColorAt(i, col);
            this._crowdBase.push({ x, y, z, sc, ph: rand() * Math.PI * 2, f: 5 + rand() * 4 });
        });
        crowd.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
        this.group.add(crowd);
        this._crowd = crowd;
        this._crowdWasJumping = false;
    }

    // ─── Overhead lighting truss ──────────────────────────────────────────────
    _buildRigging() {
        const z = 14;
        const metal = this._track(new THREE.MeshStandardMaterial({ color: 0x2a3140, roughness: 0.6, metalness: 0.6 }));
        const lampMat = this._track(new THREE.MeshStandardMaterial({ color: 0x222222, emissive: 0xf4f8ff, emissiveIntensity: 3.2 }));
        const parts = [];
        const bar = (w, h, x, y) => { const b = new THREE.BoxGeometry(w, h, 0.18); b.translate(x, y, z); parts.push(b); };
        bar(0.18, COURT_H - 4, -6, 0); bar(0.18, COURT_H - 4, 6, 0);
        bar(12, 0.18, 0, -(COURT_H - 4) / 2); bar(12, 0.18, 0, (COURT_H - 4) / 2);
        bar(12, 0.18, 0, 0);
        const truss = new THREE.Mesh(this._track(mergeGeometries(parts)), metal);
        parts.forEach(p => p.dispose());
        this.group.add(truss);

        const fixtures = [];
        [-6, 6].forEach(x => { for (let y = -12; y <= 12; y += 4) {
            const f = new THREE.BoxGeometry(0.7, 0.4, 0.12);
            f.translate(x, y, z - 0.15);
            fixtures.push(f);
        } });
        const fx = new THREE.Mesh(this._track(mergeGeometries(fixtures)), lampMat);
        fixtures.forEach(f => f.dispose());
        this.group.add(fx);
        this._rigging = [truss, fx];
    }

    // ─── Centre-hung scoreboard ───────────────────────────────────────────────
    _buildScoreboard() {
        const sb = new THREE.Group();
        sb.position.set(0, 0, 11);
        const frameMat = this._track(new THREE.MeshStandardMaterial({ color: 0x0c111a, roughness: 0.5, metalness: 0.5 }));
        const box = new THREE.Mesh(this._track(new THREE.BoxGeometry(3.2, 3.2, 1.8)), frameMat);
        sb.add(box);
        const ringMat = this._track(new THREE.MeshStandardMaterial({ color: 0x111111, emissive: 0x2f8cff, emissiveIntensity: 2.2 }));
        const ring = new THREE.Mesh(this._track(new THREE.BoxGeometry(3.3, 3.3, 0.12)), ringMat);
        ring.position.z = -0.95;
        sb.add(ring);
        this._screenMat = this._track(new THREE.MeshBasicMaterial({ color: 0xffffff }));
        const screenGeo = this._track(new THREE.PlaneGeometry(2.9, 1.45));
        for (let i = 0; i < 4; i++) {
            const s = new THREE.Mesh(screenGeo, this._screenMat);
            const a = i * Math.PI / 2;
            s.rotation.set(Math.PI / 2, 0, 0);
            s.rotation.order = 'ZXY';
            s.rotation.z = a;
            s.position.set(Math.sin(a) * 1.61, -Math.cos(a) * 1.61, 0);
            sb.add(s);
        }
        this.group.add(sb);
        this._scoreboard = sb;
    }

    setTitle(title, subtitle) {
        if (this._screenTex) this._screenTex.dispose();
        this._screenTex = makeScoreboardTexture(title || 'TÁCTICA', subtitle || '');
        this._screenMat.map = this._screenTex;
        this._screenMat.needsUpdate = true;
    }

    // Toggle overhead objects (scoreboard/truss) when the camera is looking straight down
    setOverheadVisible(v) {
        this._scoreboard.visible = v;
        this._rigging.forEach(r => { r.visible = v; });
    }

    // Pooled spotlights look great at broadcast angles but wash out the top-down view
    setSpotScale(k) {
        this._spots.forEach(s => { s.intensity = s.userData.base * k; });
    }

    setShadowQuality(size) {
        const sh = this.keyLight.shadow;
        if (sh.mapSize.x === size) return;
        sh.mapSize.set(size, size);
        if (sh.map) { sh.map.dispose(); sh.map = null; }
    }

    // ─── Per-frame ────────────────────────────────────────────────────────────
    // goal: { strength 0..1 (celebration), impact: {x, z, age} | null }
    update(realTime, goal) {
        const top = this._goals[0];
        const glow = goal ? goal.strength : 0;
        const flash = glow > 0 ? (0.6 + 0.4 * Math.sin(realTime * 14)) * glow : 0;
        top.lampMat.emissiveIntensity = 0.08 + flash * 6;
        this.goalLight.intensity = flash * 6;

        // Net ripple
        const impact = goal && goal.impact;
        const amp = impact ? Math.exp(-impact.age * 3.2) * 0.32 : 0;
        if (amp > 0.002 || top.rippleAmp > 0.002) {
            const pos = top.bag.geometry.attributes.position;
            const base = top.basePos;
            for (let i = 0; i < pos.count; i++) {
                const bx = base[i * 3], by = base[i * 3 + 1], bz = base[i * 3 + 2];
                let d = 0;
                if (amp > 0.002) {
                    const dx = bx - impact.x, dz = bz - impact.z;
                    const r2 = dx * dx + dz * dz;
                    d = amp * Math.exp(-r2 / 0.45) * Math.cos(impact.age * 22 - Math.sqrt(r2) * 7);
                }
                pos.setXYZ(i, bx, by + d, bz + d * 0.25);
            }
            pos.needsUpdate = true;
            top.bag.geometry.computeVertexNormals();
            top.rippleAmp = amp;
        }

        // Crowd jumps during a goal celebration
        const jumping = glow > 0.01;
        if (jumping || this._crowdWasJumping) {
            const mtx = new THREE.Matrix4();
            this._crowdBase.forEach((c, i) => {
                const j = jumping ? Math.max(0, Math.sin(realTime * c.f + c.ph)) * 0.25 * glow : 0;
                mtx.makeScale(c.sc, c.sc, c.sc).setPosition(c.x, c.y, c.z + j);
                this._crowd.setMatrixAt(i, mtx);
            });
            this._crowd.instanceMatrix.needsUpdate = true;
            this._crowdWasJumping = jumping;
        }
    }

    dispose() {
        this.scene.remove(this.group);
        this._disposables.forEach(d => d && d.dispose && d.dispose());
        if (this._screenTex) this._screenTex.dispose();
        this._crowd.dispose();
    }
}
